import type { ArenaConfig } from "../config/gameplay-config";
import type { RandomSource } from "./seeded-random";
import type { Vector2 } from "./player-movement";

export interface RespawnObstacle {
  readonly position: Vector2;
  readonly radius: number;
}

export interface RespawnSelectionInput {
  readonly arena: ArenaConfig;
  readonly candidates: readonly Vector2[];
  readonly fallback: Vector2;
  readonly obstacles: readonly RespawnObstacle[];
  readonly playerRadius: number;
  readonly safeDistance: number;
  readonly spawnInset: number;
}

export interface RespawnSelection {
  readonly position: Vector2;
  readonly strategy: "candidate" | "fallback";
}

const finitePosition = (position: Vector2): boolean =>
  Number.isFinite(position.x) && Number.isFinite(position.y);

const insideArena = (
  position: Vector2,
  input: RespawnSelectionInput,
): boolean => {
  const inset =
    input.arena.boundaryInset + input.spawnInset + input.playerRadius;
  return (
    position.x >= inset &&
    position.x <= input.arena.width - inset &&
    position.y >= inset &&
    position.y <= input.arena.height - inset
  );
};

const safelyClear = (
  position: Vector2,
  input: RespawnSelectionInput,
): boolean =>
  input.obstacles.every(
    (obstacle) =>
      Math.hypot(
        obstacle.position.x - position.x,
        obstacle.position.y - position.y,
      ) >=
      input.playerRadius + obstacle.radius + input.safeDistance,
  );

const validInput = (input: RespawnSelectionInput): boolean =>
  Number.isFinite(input.playerRadius) &&
  input.playerRadius > 0 &&
  Number.isFinite(input.safeDistance) &&
  input.safeDistance >= 0 &&
  Number.isFinite(input.spawnInset) &&
  input.spawnInset >= 0 &&
  input.obstacles.every(
    (obstacle) =>
      finitePosition(obstacle.position) &&
      Number.isFinite(obstacle.radius) &&
      obstacle.radius > 0,
  );

export const selectSafeRespawnPosition = (
  input: RespawnSelectionInput,
): RespawnSelection | null => {
  if (!validInput(input)) {
    return null;
  }
  const candidate = input.candidates.find(
    (position) =>
      finitePosition(position) &&
      insideArena(position, input) &&
      safelyClear(position, input),
  );
  if (candidate !== undefined) {
    return { position: { ...candidate }, strategy: "candidate" };
  }
  if (
    finitePosition(input.fallback) &&
    insideArena(input.fallback, input) &&
    safelyClear(input.fallback, input)
  ) {
    return { position: { ...input.fallback }, strategy: "fallback" };
  }
  return null;
};

export const createRespawnCandidates = (
  arena: ArenaConfig,
  spawnInset: number,
  playerRadius: number,
  columns: number,
  rows: number,
  random: Pick<RandomSource, "nextInteger">,
): readonly Vector2[] => {
  if (
    !Number.isSafeInteger(columns) ||
    columns <= 0 ||
    !Number.isSafeInteger(rows) ||
    rows <= 0
  ) {
    return [];
  }
  const inset = arena.boundaryInset + spawnInset + playerRadius;
  const width = arena.width - inset * 2;
  const height = arena.height - inset * 2;
  if (width < 0 || height < 0) {
    return [];
  }
  const candidates: Vector2[] = [];
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      candidates.push({
        x: inset + (width * (column + 0.5)) / columns,
        y: inset + (height * (row + 0.5)) / rows,
      });
    }
  }
  const offset = random.nextInteger(0, candidates.length);
  return [...candidates.slice(offset), ...candidates.slice(0, offset)];
};

export const reduceRespawnLevel = (
  level: number,
  levelLoss: number,
  maximumLevel: number,
): number => {
  if (
    !Number.isSafeInteger(level) ||
    level < 1 ||
    level > maximumLevel ||
    !Number.isSafeInteger(levelLoss) ||
    levelLoss < 0 ||
    !Number.isSafeInteger(maximumLevel) ||
    maximumLevel < 1
  ) {
    throw new RangeError("Respawn level input is invalid");
  }
  return Math.max(1, level - levelLoss);
};

export const respawnRadiusForLevel = (
  level: number,
  sizes: readonly number[],
): number => {
  const radius = sizes[level - 1];
  if (!Number.isSafeInteger(level) || level < 1 || radius === undefined) {
    throw new RangeError("Respawn level is outside the size table");
  }
  return radius;
};
